import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Download, Printer, RefreshCw, Save, Trash2, Upload } from 'lucide-react';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';
import { resolveImageUrl } from '../../services/imageUrlService';
import { downloadPrintHeaderProfile, getDefaultPrintHeaderProfile } from '../../services/printHeaderStorage';

const MAX_LOGO_SIZE_BYTES = 2 * 1024 * 1024;
/** Matches tailwind `primary` for print HTML */
const ACCENT = '#46955A';

const escapeHtml = (value) =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const readSavedHeaderHtml = (companyProfile) => {
  const p = companyProfile || {};
  return String(
    p.PrintHeader || p.PDFHeaderTemplate || p.pdfHeaderTemplate || p.PdfHeaderTemplate || ''
  );
};

const mapCompanyProfileToHeader = (companyProfile, apiBaseUrl) => {
  const address = String(companyProfile?.Address || '').trim();
  const parts = address ? address.split(',').map((x) => x.trim()).filter(Boolean) : [];
  const addressLine1 = parts[0] || '';
  const addressLine2 = parts.slice(1).join(', ');

  return {
    companyName: String(companyProfile?.Name || ''),
    addressLine1,
    addressLine2,
    phone: String(companyProfile?.PhoneNo || companyProfile?.MobileNo || ''),
    email: String(companyProfile?.Email || ''),
    website: String(companyProfile?.Website || ''),
    registrationNo: String(companyProfile?.TRNNo || ''),
    logoDataUrl: resolveImageUrl(companyProfile?.Logo || companyProfile?.PrintLogoUrl, apiBaseUrl),
    templateHtml: readSavedHeaderHtml(companyProfile)
  };
};

const templateOptions = [
  { id: 'template-1', name: 'Classic Left Logo' },
  { id: 'template-2', name: 'Logo Right Split' },
  { id: 'template-3', name: 'Centered Header' },
  { id: 'template-4', name: 'Boxed Header' },
  { id: 'template-5', name: 'Double Line Header' }
];

function isSaveSuccess(response) {
  if (response === true || response === 'true' || response === 'Success') return true;
  if (response && typeof response === 'object') {
    if (response.success === true || response.Success === true) return true;
  }
  return false;
}

function PrintHeaderPage() {
  const filePickerRef = useRef(null);
  const importFileRef = useRef(null);
  const [formData, setFormData] = useState(() => getDefaultPrintHeaderProfile());
  const [saving, setSaving] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [apiBaseUrl, setApiBaseUrl] = useState('');
  const [companyProfileSnapshot, setCompanyProfileSnapshot] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (error) {
      const t = setTimeout(() => setError(''), 6000);
      return () => clearTimeout(t);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(''), 4000);
      return () => clearTimeout(t);
    }
  }, [success]);

  const lastUpdated = useMemo(() => {
    if (!formData.updatedAt) return 'Not saved yet';
    const date = new Date(formData.updatedAt);
    return Number.isNaN(date.getTime()) ? 'Not saved yet' : date.toLocaleString();
  }, [formData.updatedAt]);

  const updateField = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const loadFromCompanyProfile = async (showSuccessToast = false) => {
    setProfileLoading(true);
    setError('');
    try {
      const base = getApiBaseUrl();
      setApiBaseUrl(base || '');
      const response = await MainService.GetCompanyProfile();
      const raw = response != null && typeof response === 'object' ? { ...response } : {};
      setCompanyProfileSnapshot(raw);
      const mapped = mapCompanyProfileToHeader(raw, base || '');
      setFormData((prev) => ({
        ...prev,
        ...mapped
      }));
      if (showSuccessToast) {
        setSuccess('Header fields loaded from company profile.');
      }
    } catch (err) {
      console.error('Failed to load company profile for print header:', err);
      setError('Failed to load company profile.');
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    loadFromCompanyProfile(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previewLogoUrl = useMemo(
    () => resolveImageUrl(formData.logoDataUrl, apiBaseUrl),
    [formData.logoDataUrl, apiBaseUrl]
  );

  const buildTemplateHtml = (templateId) => {
    const safe = {
      companyName: escapeHtml(formData.companyName || 'Company Name'),
      addressLine1: escapeHtml(formData.addressLine1 || ''),
      addressLine2: escapeHtml(formData.addressLine2 || ''),
      phone: escapeHtml(formData.phone || ''),
      email: escapeHtml(formData.email || ''),
      website: escapeHtml(formData.website || ''),
      registrationNo: escapeHtml(formData.registrationNo || ''),
      note: escapeHtml(formData.note || ''),
      customText: escapeHtml(formData.customText || '').replace(/\n/g, '<br/>')
    };

    const logoBlock = previewLogoUrl
      ? `<img src="${escapeHtml(previewLogoUrl)}" alt="Logo" style="width:${Number(formData.logoWidth) || 86}px;height:${Number(formData.logoHeight) || 86}px;object-fit:contain;" />`
      : `<div style="width:${Number(formData.logoWidth) || 86}px;height:${Number(formData.logoHeight) || 86}px;border:1px dashed #d1d5db;border-radius:4px;display:flex;align-items:center;justify-content:center;color:#6b7280;font-size:11px;">No Logo</div>`;

    const contactLine = [safe.phone, safe.email, safe.website].filter(Boolean).join(' | ');
    const addressLine = [safe.addressLine1, safe.addressLine2].filter(Boolean).join(', ');

    if (templateId === 'template-2') {
      return `
        <div style="padding-bottom:10px;border-bottom:2px solid ${ACCENT};">
          <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:16px;">
            <div style="text-align:${formData.textAlign || 'left'};flex:1;">
              <h2 style="margin:0;color:${ACCENT};">${safe.companyName}</h2>
              <div style="margin-top:6px;color:#374151;">${addressLine}</div>
              <div style="margin-top:6px;color:#374151;">${contactLine}</div>
            </div>
            <div>${logoBlock}</div>
          </div>
          <div style="margin-top:8px;color:#374151;">TRN: ${safe.registrationNo}</div>
          <div style="margin-top:6px;color:#6b7280;">${safe.note}</div>
          <div style="margin-top:6px;color:#111827;">${safe.customText}</div>
        </div>
      `;
    }

    if (templateId === 'template-3') {
      return `
        <div style="padding-bottom:10px;border-bottom:2px solid ${ACCENT};">
          <div style="display:flex;flex-direction:column;align-items:center;text-align:center;gap:8px;">
            <div>${logoBlock}</div>
            <h2 style="margin:0;color:${ACCENT};">${safe.companyName}</h2>
            <div style="color:#374151;">${addressLine}</div>
            <div style="color:#374151;">${contactLine}</div>
            <div style="color:#374151;">TRN: ${safe.registrationNo}</div>
            <div style="color:#6b7280;">${safe.note}</div>
            <div style="color:#111827;">${safe.customText}</div>
          </div>
        </div>
      `;
    }

    if (templateId === 'template-4') {
      return `
        <div style="padding:10px;border:1px solid #d1d5db;border-radius:8px;">
          <div style="display:flex;align-items:flex-start;gap:14px;">
            <div>${logoBlock}</div>
            <div style="flex:1;text-align:${formData.textAlign || 'left'};">
              <h2 style="margin:0 0 4px 0;color:${ACCENT};">${safe.companyName}</h2>
              <div style="color:#374151;">${contactLine}</div>
              <div style="margin-top:4px;color:#374151;">${addressLine}</div>
              <div style="margin-top:4px;color:#374151;">TRN: ${safe.registrationNo}</div>
            </div>
          </div>
          <div style="margin-top:8px;padding-top:8px;border-top:1px dashed #d1d5db;color:#6b7280;">${safe.note}</div>
          <div style="margin-top:6px;color:#111827;">${safe.customText}</div>
        </div>
      `;
    }

    if (templateId === 'template-5') {
      return `
        <div style="padding-bottom:10px;border-bottom:3px double ${ACCENT};">
          <div style="display:grid;grid-template-columns:auto 1fr;gap:12px;align-items:center;">
            <div>${logoBlock}</div>
            <div style="text-align:${formData.textAlign || 'left'};">
              <h2 style="margin:0;color:${ACCENT};">${safe.companyName}</h2>
              <div style="margin-top:4px;color:#374151;">${addressLine}</div>
              <div style="margin-top:4px;color:#374151;">${contactLine}</div>
            </div>
          </div>
          <div style="display:flex;justify-content:space-between;gap:10px;margin-top:8px;">
            <div style="color:#374151;">TRN: ${safe.registrationNo}</div>
            <div style="color:#6b7280;">${safe.note}</div>
          </div>
          <div style="margin-top:6px;color:#111827;">${safe.customText}</div>
        </div>
      `;
    }

    return `
      <div style="display:flex;align-items:flex-start;gap:16px;padding-bottom:10px;border-bottom:2px solid ${ACCENT};">
        <div>${logoBlock}</div>
        <div style="flex:1;text-align:${formData.textAlign || 'left'};">
          <h2 style="margin:0;color:${ACCENT};">${safe.companyName}</h2>
          <div style="margin-top:4px;color:#374151;">${addressLine}</div>
          <div style="margin-top:4px;color:#374151;">${contactLine}</div>
          <div style="margin-top:4px;color:#374151;">TRN: ${safe.registrationNo}</div>
          <div style="margin-top:4px;color:#6b7280;">${safe.note}</div>
          <div style="margin-top:6px;color:#111827;">${safe.customText}</div>
        </div>
      </div>
    `;
  };

  const selectedTemplateHtml = useMemo(() => {
    const stored = String(formData.templateHtml || '').trim();
    if (stored) return stored;
    return buildTemplateHtml(formData.selectedTemplateId || 'template-1');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, previewLogoUrl]);

  const handleLogoSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file for the logo.');
      event.target.value = '';
      return;
    }

    if (file.size > MAX_LOGO_SIZE_BYTES) {
      setError('Logo file size should be less than 2 MB.');
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      updateField('logoDataUrl', String(reader.result || ''));
      setSuccess('Logo selected successfully.');
    };
    reader.onerror = () => setError('Failed to read selected logo file.');
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const handleSave = async () => {
    if (!companyProfileSnapshot || typeof companyProfileSnapshot !== 'object') {
      setError('Company profile is not loaded. Use “Load Company Profile” and try again.');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        ...companyProfileSnapshot,
        PrintHeader: selectedTemplateHtml
      };

      const response = await MainService.SaveCompanyProfile(payload);

      if (!isSaveSuccess(response)) {
        setError('Failed to save print header. Please check the server response.');
        return;
      }

      setCompanyProfileSnapshot((prev) =>
        prev && typeof prev === 'object' ? { ...prev, PrintHeader: selectedTemplateHtml } : prev
      );
      setFormData((prev) => ({
        ...prev,
        templateHtml: selectedTemplateHtml,
        updatedAt: new Date().toISOString()
      }));
      setSuccess('Print header saved successfully.');
    } catch (err) {
      console.error('Failed to save print header:', err);
      setError('Failed to save print header.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setFormData(getDefaultPrintHeaderProfile());
    setSuccess('Form reset to default values.');
  };

  const handleExport = () => {
    try {
      downloadPrintHeaderProfile(formData);
      setSuccess('Header profile exported to a JSON file.');
    } catch (err) {
      console.error('Failed to export profile:', err);
      setError('Failed to export profile.');
    }
  };

  const handleImport = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result || '{}'));
        setFormData({
          ...getDefaultPrintHeaderProfile(),
          ...parsed
        });
        setSuccess('Header profile imported. Click Save to persist it to the server.');
      } catch {
        setError('Invalid file format. Please import a valid JSON file.');
      }
    };
    reader.onerror = () => setError('Failed to read imported file.');
    reader.readAsText(file);
    event.target.value = '';
  };

  const btnOutline =
    'inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50';
  const btnPrimary =
    'inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:opacity-90 disabled:opacity-50';
  const btnSecondary =
    'inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-gray-100 text-gray-800 hover:bg-gray-200';

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-3">
            <Printer className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Print Header</h1>
              <p className="text-gray-600 mt-1">
                Choose a template, edit company details, then save. Stored on the company profile as{' '}
                <code className="text-sm bg-gray-100 px-1 rounded">PrintHeader</code>.
              </p>
              <p className="text-sm text-gray-500 mt-2">Last updated: {lastUpdated}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={btnOutline}
              onClick={() => loadFromCompanyProfile(true)}
              disabled={profileLoading}
            >
              <RefreshCw className="w-4 h-4" />
              {profileLoading ? 'Loading…' : 'Load Company Profile'}
            </button>
            <button type="button" className={btnOutline} onClick={() => importFileRef.current?.click()}>
              <Upload className="w-4 h-4" />
              Import
            </button>
            <button type="button" className={btnOutline} onClick={handleExport}>
              <Download className="w-4 h-4" />
              Export
            </button>
            <button type="button" className={btnSecondary} onClick={handleReset}>
              <Trash2 className="w-4 h-4" />
              Reset
            </button>
            <button type="button" className={btnPrimary} onClick={handleSave} disabled={saving}>
              <Save className="w-4 h-4" />
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        {error ? (
          <div className="mt-4 rounded-lg bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm">{error}</div>
        ) : null}
        {success ? (
          <div className="mt-4 rounded-lg bg-green-50 border border-green-200 text-green-800 px-4 py-3 text-sm">
            {success}
          </div>
        ) : null}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
            <input
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={formData.companyName}
              onChange={(e) => updateField('companyName', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Registration No (TRN)</label>
            <input
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={formData.registrationNo}
              onChange={(e) => updateField('registrationNo', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={formData.phone}
              onChange={(e) => updateField('phone', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={formData.email}
              onChange={(e) => updateField('email', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
            <input
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={formData.website}
              onChange={(e) => updateField('website', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
            <input
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={formData.addressLine1}
              onChange={(e) => updateField('addressLine1', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
            <input
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={formData.addressLine2}
              onChange={(e) => updateField('addressLine2', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Header Note</label>
            <input
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={formData.note}
              onChange={(e) => updateField('note', e.target.value)}
            />
          </div>
        </div>

        <div className="border-t border-gray-200 pt-4 flex flex-wrap items-center gap-2">
          <button type="button" className={btnOutline} onClick={() => filePickerRef.current?.click()}>
            <Upload className="w-4 h-4" />
            Upload Logo
          </button>
          {formData.logoDataUrl ? (
            <button type="button" className={btnSecondary} onClick={() => updateField('logoDataUrl', '')}>
              <Trash2 className="w-4 h-4" />
              Remove Logo
            </button>
          ) : null}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Text align</label>
            <select
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={formData.textAlign || 'left'}
              onChange={(e) => updateField('textAlign', e.target.value)}
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Logo width (px)</label>
            <input
              type="number"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={formData.logoWidth || 86}
              min={20}
              max={300}
              onChange={(e) => updateField('logoWidth', Number(e.target.value) || 86)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Logo height (px)</label>
            <input
              type="number"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={formData.logoHeight || 86}
              min={20}
              max={300}
              onChange={(e) => updateField('logoHeight', Number(e.target.value) || 86)}
            />
          </div>
          <div className="md:col-span-2 xl:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Custom text</label>
            <textarea
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              rows={3}
              value={formData.customText || ''}
              onChange={(e) => updateField('customText', e.target.value)}
              placeholder="Additional lines (saved inside the HTML template)"
            />
          </div>
        </div>

        <input ref={filePickerRef} type="file" accept="image/*" className="hidden" onChange={handleLogoSelect} />
        <input ref={importFileRef} type="file" accept="application/json" className="hidden" onChange={handleImport} />

        <div className="border-t border-gray-200 pt-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Choose header template</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
            {templateOptions.map((template) => {
              const active = (formData.selectedTemplateId || 'template-1') === template.id;
              return (
                <button
                  key={template.id}
                  type="button"
                  className={`text-left rounded-lg border p-2 transition-colors ${
                    active ? 'border-primary ring-2 ring-primary/30' : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      selectedTemplateId: template.id,
                      templateHtml: ''
                    }))
                  }
                >
                  <div className="flex justify-between items-center border-b border-gray-100 pb-2 mb-2 px-1">
                    <span className="font-medium text-sm text-gray-900">{template.name}</span>
                    {active ? <span className="text-xs text-primary font-medium">Selected</span> : null}
                  </div>
                  <div className="overflow-hidden" style={{ maxHeight: 200 }}>
                    <div
                      className="origin-top-left scale-[0.82] w-[122%]"
                      dangerouslySetInnerHTML={{ __html: buildTemplateHtml(template.id) }}
                    />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">Selected template output</h3>
          <div
            className="border border-gray-200 rounded-md p-4 bg-white overflow-x-auto"
            dangerouslySetInnerHTML={{ __html: selectedTemplateHtml }}
          />
        </div>
      </div>
    </div>
  );
}

export default PrintHeaderPage;
